import {LocalitySelectedType, UserType, ZoneType} from "data/types";
import React, {FC, useEffect, useState} from "react";
import {
  CheckIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";

import SeatSvg from "../../shared/Seat/SeatSvg";
import {useSocket} from "hooks/useSocket";
import {getEnvVariables} from "../../helpers/getEnvVariables";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import SeatsBadges from "./SeatsBadges";
import {SEAT_STATUS, SeatStatus} from "./seatStatus";
import {useDeleteSeatsByLocalityMutation} from "store/api";

export const Seats: FC<{
  zone: ZoneType;
  eventId: string;
  maxSeats: number;
  endTime: string;
  localitySelected: LocalitySelectedType[];
  setLocalitySelected: React.Dispatch<
    React.SetStateAction<LocalitySelectedType[]>
  >;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpenedZones: React.Dispatch<React.SetStateAction<boolean>>;
  zonesQuantity: number;
  handleClose: () => Promise<void>;
}> = ({
  zone,
  eventId,
  maxSeats,
  endTime,
  setIsOpen,
  localitySelected,
  setLocalitySelected,
  setIsOpenedZones,
  zonesQuantity,
  handleClose,
}) => {
  const [alert, setAlert] = useState<string | null>(null);
  const [seatsNormalPurchased, setSeatsNormalPurchased] = useState<string[]>(
    [],
  );
  const [seatsSubscriberPurchased, setSeatsSubscriberPurchased] = useState<
    string[]
  >([]);
  const [seatsBuying, setSeatsBuying] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const {REACT_APP_URL} = getEnvVariables();
  const userData = localStorage.getItem("user");
  const [deleteSeats] = useDeleteSeatsByLocalityMutation();
  const [isLoading, setIsLoading] = useState(true);

  let user: UserType;
  if (userData) {
    user = JSON.parse(userData) as UserType;
  }
  const socket = useSocket(`${REACT_APP_URL}ticket-select`, {
    id: zone._id,
    customer: user!._id,
  });
  socket.emit("subscribe-seat", {id: zone._id, customer: user!._id});
  useEffect(() => {
    socket.on("ticket-select", (data: any) => {
      console.log(data);
      switch (data.type) {
        case "init":
          setIsLoading(false);
          if (data.buyNormalSeat.length > 0) {
            setSeatsNormalPurchased(
              data.buyNormalSeat[0].positions.map((seat: any) => seat.list),
            );
          }
          if (data.buySeat.length > 0) {
            setSeatsSubscriberPurchased(
              data.buySeat[0].position.map((seat: any) => seat.list),
            );
          }
          if (data.selectSeat.length > 0) {
            console.log(localitySelected);
            const seatsSocket = data.selectSeat[0].position.map(
              (seat: any) => seat,
            );
            const ownSelectedSeatsZone = localitySelected
              .find((loc) => loc._id === zone.locality)
              ?.zones?.find((z) => z._id === zone._id)?.seats;
            const ownSelectedSeats =
              ownSelectedSeatsZone?.filter((item: any) =>
                seatsSocket.includes(item),
              ) || [];
            const buyingSeats = seatsSocket.filter(
              (item: any) => !ownSelectedSeats.includes(item),
            );
            setLocalitySelected((prev) =>
              prev.map((item) =>
                item._id === zone.locality
                  ? {
                      ...item,
                      zones: item.zones?.map((zoneItem) =>
                        zoneItem._id === zone._id
                          ? {
                              ...zoneItem,
                              seats: Array.from(
                                new Set([
                                  ...zoneItem.seats,
                                  ...ownSelectedSeats,
                                ]),
                              ),
                            }
                          : zoneItem,
                      ),
                    }
                  : item,
              ),
            );
            setSeatsBuying(buyingSeats);
          }
          break;
        case "reserveSeatByTopic":
          if (data.customer !== user._id) {
            setSeatsBuying((prev) => [...prev, data.selectSeat.join()]);
          }
          break;
        case "cancelSelectedSeat":
          if (data.selectSeat) {
            setSeatsBuying((prev) =>
              prev.filter((item) => item !== data.selectSeat),
            );
          }
          break;
        case "cancelSeatByTopic":
          if (data.selectSeat) {
            const seatsToRemove = data.selectSeat[0].position.map(
              (seat: any) => seat,
            );
            setSeatsBuying((prev) =>
              prev.filter((item) => !seatsToRemove.includes(item)),
            );
          }
          break;
      }
    });

    return () => {
      if (!isSaving) {
        cancelSeatByTopic();
        socket.disconnect();
      }
    };
  }, [socket]);

  const cancelSeatByTopic = () => {
    socket.emit("ticket-select", {
      topic: zone._id,
      customer: user._id,
      type: "cancelSeatByTopic",
    });
    // setSelected((prev) =>
    // prev.map((item) =>
    //   item._id === zone._id ? { ...item, seats: [] } : item
    // ));
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      deleteSeats(zone.locality);
      event.preventDefault();
      event.returnValue = "";
      setIsSaving(false);
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const handleSelect = (seatData: string, seatNumber: string) => {
    setLocalitySelected((prev) => {
      const localityIndex = prev.findIndex(
        (item) => item._id === zone.locality,
      );
      const zoneIndex = prev?.[localityIndex]?.zones?.findIndex(
        (item) => item._id === zone._id,
      );

      // Si el asiento ya está seleccionado en esta zona, lo quitamos
      if (prev[localityIndex].zones?.[zoneIndex].seats.includes(seatData)) {
        socket.emit("ticket-select", {
          type: "cancelSelectedSeat",
          topic: zone._id,
          position: seatData,
          customer: user._id,
        });
        const updatedZone = {
          ...prev[localityIndex].zones?.[zoneIndex],
          seats: prev[localityIndex].zones?.[zoneIndex].seats.filter(
            (seat) => seat !== seatData,
          ),
          numbers: prev[localityIndex].zones?.[zoneIndex].numbers.filter(
            (number) => number !== seatNumber,
          ),
          customers:
            prev[localityIndex].zones?.[zoneIndex].customers?.slice(0, -1) ||
            [],
        };
        const updatedLocality = {
          ...prev[localityIndex],
          zones: prev[localityIndex].zones?.map((item) =>
            item._id === zone._id ? updatedZone : item,
          ),
        };
        const newState = [
          ...prev.slice(0, localityIndex),
          updatedLocality,
          ...prev.slice(localityIndex + 1),
        ];
        return newState;
      }

      const totalSeatsSelected = prev[localityIndex].zones?.reduce(
        (total, item) => total + item.seats.length,
        0,
      );

      if (totalSeatsSelected >= maxSeats) {
        setAlert(
          maxSeats === 1
            ? "Ya elegiste tu asiento. Quítalo para elegir otro."
            : `Ya elegiste los ${maxSeats} asientos permitidos. Quita uno para cambiarlo.`,
        );
        setTimeout(() => setAlert(null), 3000);
        return prev;
      }
      console.log(seatData, seatNumber);
      console.log(...prev);
      const updatedZone = {
        ...prev[localityIndex].zones?.[zoneIndex],
        seats: [...prev[localityIndex].zones?.[zoneIndex].seats, seatData],
        numbers: [
          ...prev[localityIndex].zones?.[zoneIndex].numbers,
          seatNumber,
        ],
        customers: [
          ...(prev[localityIndex].zones?.[zoneIndex].customers || []),
          "",
        ],
      };

      const updatedLocality = {
        ...prev[localityIndex],
        zones: prev[localityIndex].zones?.map((item) =>
          item._id === zone._id ? updatedZone : item,
        ),
      };

      const newState = [
        ...prev.slice(0, localityIndex),
        updatedLocality,
        ...prev.slice(localityIndex + 1),
      ];

      socket.emit("ticket-select", {
        type: "reserveSeatByTopic",
        topic: zone._id,
        position: [seatData],
        endTime: endTime,
        locality: zone.locality,
        customer: user._id,
        event: eventId,
      });
      return newState;
    });
  };

  const handleSave = () => {
    setIsSaving(true);
    socket.emit("unsubscribe-seat", {
      topic: zone.locality,
      customer: user._id,
    });
    setIsOpen(false);
    socket.disconnect();
    if (zonesQuantity === 1) {
      setIsOpenedZones(false);
    }
  };

  const handleCancel = () => {
    setIsSaving(false);
    socket.emit("ticket-select", {
      topic: zone._id,
      customer: user._id,
      type: "cancelSeatByTopic",
    });

    setLocalitySelected((prev) => {
      const localityIndex = prev.findIndex(
        (item) => item._id === zone.locality,
      );
      const zoneIndex = prev?.[localityIndex]?.zones?.findIndex(
        (item) => item._id === zone._id,
      );
      const updatedZone = {
        ...prev[localityIndex].zones?.[zoneIndex],
        seats: [],
        numbers: [],
      };
      const updatedLocality = {
        ...prev[localityIndex],
        zones: prev[localityIndex].zones?.map((item) =>
          item._id === zone._id ? updatedZone : item,
        ),
      };
      const newState = [
        ...prev.slice(0, localityIndex),
        updatedLocality,
        ...prev.slice(localityIndex + 1),
      ];
      return newState;
    });
    setIsOpen(false);
    if (zonesQuantity === 1) {
      handleClose();
    }
  };

  if (isLoading) {
    return (
      <div
        role="status"
        className="flex flex-col items-center justify-center gap-3 py-20 text-sm text-neutral-500 dark:text-neutral-400"
      >
        <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        Cargando mapa de asientos…
      </div>
    );
  }

  const localityZones =
    localitySelected.find((item) => item._id === zone.locality)?.zones || [];
  const seatsSelectedByZone = localityZones.find(
    (item) => item._id === zone._id,
  );
  const selectedSeats = seatsSelectedByZone?.seats || [];
  const selectedNumbers = seatsSelectedByZone?.numbers || [];
  const totalSelected = localityZones.reduce(
    (total, item) => total + item.seats.length,
    0,
  );
  const limitReached = totalSelected >= maxSeats;

  const getSeatStatus = (seatId: string): SeatStatus => {
    if (seatsNormalPurchased.includes(seatId)) return "purchased";
    if (seatsSubscriberPurchased.includes(seatId)) return "subscriber";
    if (seatsBuying.includes(seatId)) return "buying";
    if (selectedSeats.includes(seatId)) return "selected";
    return "available";
  };

  const seatSizeClass = "w-11 h-11 sm:w-12 sm:h-12 shrink-0";

  return (
    <div className="w-full flex flex-col gap-5 sm:gap-6">
      <p className="flex items-start gap-2 text-sm text-neutral-500 dark:text-neutral-400">
        <InformationCircleIcon className="w-5 h-5 shrink-0" />
        Pulsa «Guardar» para conservar los asientos seleccionados.
      </p>

      <SeatsBadges />

      <div className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-black/20">
        <div
          className="overflow-x-auto px-4 pt-5 pb-6"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor:
              localStorage.getItem("theme") === "dark"
                ? "rgba(100, 116, 139, 0.4) rgba(15, 23, 42, 0.2)"
                : "rgba(100, 116, 139, 0.4) rgba(15, 23, 42, 0.0)",
          }}
        >
          <div className="w-max mx-auto">
            <div className="mx-auto mb-10 w-2/3 min-w-[12rem]">
              <div className="h-1.5 rounded-full bg-gradient-to-r from-transparent via-neutral-400 to-transparent dark:via-neutral-500" />
              <p className="mt-2 text-center text-[11px] font-semibold uppercase tracking-[0.25em] text-neutral-400">
                Escenario
              </p>
            </div>

            <div className="space-y-1.5 sm:space-y-2">
              {[...Array(zone.rows)].map((_, rowIndex) => {
                const row = zone.seat[0].seat_map[rowIndex];
                const rowLabel = (
                  <span className="w-5 shrink-0 text-center text-xs font-semibold text-neutral-400">
                    {row.letter}
                  </span>
                );
                let counter = 0;
                return (
                  <div
                    key={rowIndex}
                    className="flex items-center gap-2 sm:gap-3"
                  >
                    {rowLabel}
                    <div className="flex gap-1.5 sm:gap-2">
                      {[...Array(zone.cols)].map((_, colIndex) => {
                        if (row.layout[colIndex] === "_")
                          return (
                            <div
                              key={colIndex}
                              className={seatSizeClass}
                              aria-hidden="true"
                            />
                          );

                        const seatId = `${rowIndex + 1}_${colIndex + 1}`;
                        const seatLabel =
                          row.letter +
                          String(row.start + counter).padStart(2, "0");
                        counter++;

                        const status = getSeatStatus(seatId);
                        const isSelected = status === "selected";
                        const isAvailable = status === "available";
                        const isUnavailable = !isSelected && !isAvailable;
                        const isDimmed = isAvailable && limitReached;

                        return (
                          <button
                            key={colIndex}
                            type="button"
                            disabled={isUnavailable}
                            aria-pressed={isSelected}
                            aria-label={`Asiento ${seatLabel}, ${SEAT_STATUS[status].text}`}
                            onClick={() => handleSelect(seatId, seatLabel)}
                            className={`group relative ${seatSizeClass} rounded-lg transition duration-150 ease-out
                              focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-800
                              ${SEAT_STATUS[status].seat}
                              ${isUnavailable ? "cursor-not-allowed" : "cursor-pointer active:scale-90"}
                              ${isAvailable && !limitReached ? "hover:-translate-y-0.5 hover:text-emerald-700 dark:hover:text-emerald-500" : ""}
                              ${isSelected ? "scale-105 drop-shadow-md" : ""}
                              ${isDimmed ? "opacity-40" : ""}`}
                          >
                            <SeatSvg backgroundColor="currentColor" />
                            <span
                              className={`absolute inset-0 flex items-center justify-center pb-2 text-[10px] sm:text-[11px] font-bold ${SEAT_STATUS[status].label}`}
                            >
                              {seatLabel}
                            </span>
                            {isSelected && (
                              <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 rounded-full bg-neutral-900 text-white ring-2 ring-neutral-50 dark:bg-white dark:text-neutral-900 dark:ring-neutral-900">
                                <CheckIcon className="w-3 h-3" />
                              </span>
                            )}
                            <span
                              className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-lg px-2.5 py-1 text-xs font-medium shadow-lg
                                bg-neutral-900 text-white dark:bg-white dark:text-neutral-900
                                opacity-0 transition-opacity duration-150 [@media(hover:hover)]:group-hover:opacity-100"
                            >
                              {seatLabel} · {SEAT_STATUS[status].text}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {rowLabel}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0 flex-1 space-y-2.5">
          <div className="flex items-baseline justify-between gap-3 sm:justify-start">
            <span className="text-sm font-medium">Asientos seleccionados</span>
            <span className="text-sm font-semibold tabular-nums">
              {totalSelected} de {maxSeats}
            </span>
          </div>
          <div className="h-1.5 w-full sm:max-w-xs overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700">
            <div
              className="h-full rounded-full bg-primary-500 transition-[width] duration-300 ease-out"
              style={{
                width: `${Math.min(100, (totalSelected / maxSeats) * 100)}%`,
              }}
            />
          </div>
          {selectedNumbers.length > 0 ? (
            <ul className="flex flex-wrap gap-2">
              {selectedNumbers.map((number, index) => (
                <li key={selectedSeats[index]}>
                  <button
                    type="button"
                    onClick={() => handleSelect(selectedSeats[index], number)}
                    aria-label={`Quitar asiento ${number}`}
                    className="inline-flex items-center gap-1 rounded-full bg-primary-100 py-1 pl-3 pr-2 text-xs font-semibold text-neutral-900 transition-colors hover:bg-primary-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                  >
                    {number}
                    <XMarkIcon className="w-3.5 h-3.5 opacity-60" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Toca un asiento disponible para seleccionarlo.
            </p>
          )}
          <p
            role="status"
            aria-live="polite"
            className="min-h-[1.25rem] text-sm font-medium text-red-600 dark:text-red-400"
          >
            {alert}
          </p>
        </div>
        <div className="flex gap-3 sm:shrink-0">
          <ButtonSecondary
            className="flex-1 sm:flex-none"
            onClick={handleCancel}
          >
            Cancelar
          </ButtonSecondary>
          <ButtonPrimary className="flex-1 sm:flex-none" onClick={handleSave}>
            {selectedSeats.length > 0
              ? `Guardar (${selectedSeats.length})`
              : "Guardar"}
          </ButtonPrimary>
        </div>
      </div>
    </div>
  );
};
